<script setup>
import EntityPopoverFirm from '@/components/EntityPopoverFirm.vue'
import EntityPopoverLawyer from '@/components/EntityPopoverLawyer.vue'

const props = defineProps({
  entity: {
    type: Object,
    default: () => {}
  }
})

const iconRef = ref(null)
const cardRef = ref(null)
const popoverRef = ref(null)
const { isDesktopView, isMobileView } = useScreen()

useEventListener(document, 'mousemove', () => isDesktopView.value ? popoverRef.value?.close() : null)
</script>

<template>
  <div ref="cardRef" class="flex flex-col items-center gap-5 flex-wrap justify-center">
    <UseEntity :entity="entity" #="{ name, bio, image, avg_price, rating, link, type, specialisations }">
      <div class="bg-white-500 rounded-[20px] md:rounded-[40px] h-full w-full md:aspect-[87.4/100] relative flex flex-col md:p-3">
      
        <EntityPopover ref="popoverRef" :propRef="iconRef" :cardRef="cardRef" class="z-[100]">
          <template #reference="{ open }">
            <div class="absolute top-3 md:top-1 left-3 md:left-1 flex items-center justify-center">
              <button
                ref="iconRef"
                class="bg-gray-50 rounded-full p-1 md:p-1.5 text-darkBlue flex items-center justify-center focus:outline-none"
                :disabled="isDesktopView"
                @click="open()"
                @mouseover="isDesktopView ? open() : null"
              >
                <IconInfo 
                  class="size-4.5 md:size-7"
                  alt="Info"
                />
              </button>
            </div>
          </template>

          <template #default>
            <component
              :is="(({ firm: EntityPopoverFirm })[type]) || EntityPopoverLawyer"
              :entity="props.entity"
            />
          </template>
        </EntityPopover>
 
        <div class="md:absolute top-0 left-0 w-full h-full">
          <div class="flex justify-center h-full w-full">
            <div class="relative h-full w-full *:md:absolute *:top-0 *:left-0 *:w-full *:h-full *:object-cover *:object-center flex justify-center items-center">
              <GTImage
                :src="image"
                :alt="type === 'lawyer'? 'lawyer photo' : 'law firm logo'"
                class="rounded-t-[20px] md:rounded-[40px] max-h-full max-w-full aspect-[87.4/100]"
              >
                <template #fallback>
                  <div v-if="type === 'lawyer'" class="overflow-hidden">
                    <img src="/images/lawyer.svg" alt="lawyer" class="translate-y-4 xl:translate-y-8 md:max-w-[330px] md:max-h-[330px] mx-auto">
                  </div>
                  <div v-else-if="type === 'firm'">
                    <img src="/images/firm.svg" alt="Firm" class="translate-y-4 xl:translate-y-8 md:max-w-[330px] md:max-h-[330px] mx-auto">
                  </div>
                </template>
              </GTImage>
              <NuxtLink
                :to="link"
                aria-label="Open profile"
                class="absolute inset-0 rounded-t-[20px] md:rounded-[40px] focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div class="mt-auto group">
          <div class="bg-lightBlue rounded-b-[20px] md:rounded-[28px] p-3 pb-4 sm:px-4 sm:pt-4 sm:pb-5 text-white relative min-h-[33%] h-[184px] lg:h-[152px] font-normal relative transition-all duration-300 group-hover:h-[255px] md:group-hover:h-[270px] overflow-hidden">
            <div class="grid gap-0.5 leading-none md:leading-normal">
              <div class="md:flex justify-between gap-2 truncate md:whitespace-normal">
                <div class="flex-1 truncate md:whitespace-normal">
                  <NuxtLink
                    :to="link"
                    class="text-18 lg:text-2xl font-redhat font-medium text-white md:line-clamp-2 truncate md:whitespace-normal hover:underline leading-none"
                  >
                    {{ name }}
                  </NuxtLink>
                </div>
              </div>

              <!-- <div v-if="bio && type !== 'firm'" class="text-xs lg:text-sm leading-4 md:leading-6 font-light line-clamp-2 md:line-clamp-1 md:max-h-[1.5rem]">
                {{ bio }}
              </div> -->
              <div v-if="specialisations && specialisations.length > 0 && type !== 'firm'" class="text-xs lg:text-sm leading-4 md:leading-6 font-light line-clamp-1 transition-all duration-300 overflow-hidden group-hover:line-clamp-none group-hover:overflow-auto group-hover:mt-2 group-hover:space-y-0.5">
                <div class="hidden md:group-hover:block font-medium text-sm mb-1">
                  {{ $t('areas_of_specialization') }}:
                </div>

                <template v-for="s in (isMobileView ? specialisations.slice(0, 3) : specialisations)" :key="s.id">
                  <div>{{ s?.specialisation?.name }}</div>
                </template>
              </div>

              <rating-stars :value="rating" :textSize="isMobileView ? 12 : 16" class="mt-1 md:-mt-0.5" />
            </div>

            <div class="absolute lg:flex bottom-2 lg:bottom-3 lg:mr-2 right-0 lg:right-2 lg:items-center w-full">
              <div class="mx-2 lg:ml-8 lg:mr-3 lg:w-full lg:h-9 lg:flex">
                <div class="bg-white text-lightBlue pl-1.5 flex items-center justify-center" :class="type !== 'firm' && image? 'lg:w-[122px] lg:min-w-[122px] rounded-t-xl lg:rounded-l-xl lg:rounded-r-none' : 'w-full lg:w-[135px] rounded-xl'">
                  <div class="text-16 lg:text-[20px]">
                    {{ avg_price }}
                  </div>
                  <div class="text-[10px] ml-0.5 mt-0.5 leading-none flex lg:block">
                    <div>/{{$t('hour')}}</div>
                    <div v-if="entity.is_vat_payer" class="ml-1 lg:ml-0">({{ $t('including_vat') }})</div>
                  </div>
                </div>
                <div v-if="type !== 'firm' && image" class="border border-white w-full h-full min-h-[25px] rounded-b-xl lg:rounded-l-none lg:rounded-r-xl text-[12px] leading-[13px] lg:leading-4 py-0.5 px-2 flex flex-wrap h-full items-center gap-x-1">
                  {{ $t('introductory_meeting') }}
                  <div class="font-semibold">29€</div>
                  <div v-if="entity.is_vat_payer">({{ $t('including_vat') }})</div>
                  {{ $t('or') }}
                  <div class="font-semibold">{{ $t('free') }}</div>
                </div>
              </div>
              <NuxtLink :to="link">
                <IconArrowRightCircle class="ml-auto mt-1.5 lg:mt-0 mr-2 lg:mr-0 size-5 lg:size-8 text-white" />
              </NuxtLink>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 title absolute left-0 top-[37%] sm:top-[36%] md:top-20 rounded-r-xl w-full max-w-[98px] max-h-[52px] z-10 text-darkBlue flex items-center justify-center p-3 font-redhat font-medium text-sm">
          {{ $t(type) }}
        </div>
      </div>
    </UseEntity>
  </div>
</template>
